import type { ScoreDistributionResponse } from "@/client"
import { QuizzesService } from "@/client"
import { Checkbox } from "@/components/ui/checkbox"
import { useOrgColorScheme } from "@/hooks/useOrgColorScheme"
import { DashboardService } from "@/services/dashboardService"
import type {
  QuizSummaryForStudent,
  SubjectPercentileHistoryResponse,
} from "@/types/dashboard"
import { Box, Card, Flex, HStack, Heading, Stack, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"

function ordinalSuffix(n: number): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  if (mod10 === 1) return `${n}st`
  if (mod10 === 2) return `${n}nd`
  if (mod10 === 3) return `${n}rd`
  return `${n}th`
}

function LollipopRow({
  label,
  percentile,
  isPending,
  isClickable,
  onClick,
}: {
  label: string
  percentile: number | null
  isPending: boolean
  isClickable: boolean
  onClick?: () => void
}) {
  const { getColor, getTextColor } = useOrgColorScheme()
  const dotColor =
    percentile !== null ? getColor(Math.round(percentile)) : undefined
  const textColor =
    percentile !== null ? getTextColor(Math.round(percentile)) : undefined

  return (
    <HStack
      gap={3}
      py={2}
      px={2}
      borderRadius="md"
      opacity={isPending ? 0.5 : 1}
      cursor={isClickable ? "pointer" : "default"}
      _hover={isClickable ? { bg: "gray.50" } : undefined}
      onClick={isClickable ? onClick : undefined}
    >
      <Text fontSize="sm" w="160px" truncate title={label}>
        {label}
      </Text>
      <Box flex={1} position="relative" h="12px">
        <Box
          position="absolute"
          top="50%"
          left={0}
          right={0}
          h="1px"
          bg={isPending ? "gray.200" : "gray.300"}
          style={isPending ? { borderTop: "1px dashed #CBD5E0" } : undefined}
        />
        {percentile !== null && dotColor && (
          <Box
            position="absolute"
            top="50%"
            left={`${percentile}%`}
            transform="translate(-50%, -50%)"
            w="10px"
            h="10px"
            borderRadius="full"
            style={{
              backgroundColor: dotColor,
              border: "2px solid white",
              boxShadow: `0 0 0 1.5px ${dotColor}`,
            }}
          />
        )}
      </Box>
      <Text
        fontSize="xs"
        w="40px"
        textAlign="right"
        fontWeight="bold"
        style={{
          color: percentile !== null && textColor ? textColor : undefined,
        }}
        color={percentile !== null ? undefined : "gray.400"}
      >
        {percentile !== null ? ordinalSuffix(Math.round(percentile)) : "—"}
      </Text>
    </HStack>
  )
}

function SubjectRow({
  subjectId,
  subjectName,
  percentile,
  onSubjectClick,
}: {
  subjectId: string
  subjectName: string
  percentile: number | null
  onSubjectClick: (id: string, name: string) => void
}) {
  const { getColor, getTextColor } = useOrgColorScheme()
  const dotColor =
    percentile !== null ? getColor(Math.round(percentile)) : undefined
  const textColor =
    percentile !== null ? getTextColor(Math.round(percentile)) : undefined

  return (
    <HStack gap={3} py={1.5} pl={8} pr={2} borderRadius="md">
      <Text
        fontSize="xs"
        w="140px"
        truncate
        color="blue.500"
        cursor="pointer"
        _hover={{ textDecoration: "underline" }}
        onClick={() => onSubjectClick(subjectId, subjectName)}
        title={subjectName}
      >
        {subjectName}
      </Text>
      <Box flex={1} position="relative" h="10px">
        <Box
          position="absolute"
          top="50%"
          left={0}
          right={0}
          h="1px"
          bg="gray.200"
        />
        {percentile !== null && dotColor && (
          <Box
            position="absolute"
            top="50%"
            left={`${percentile}%`}
            transform="translate(-50%, -50%)"
            w="8px"
            h="8px"
            borderRadius="full"
            style={{
              backgroundColor: dotColor,
              border: "1.5px solid white",
              boxShadow: `0 0 0 1px ${dotColor}`,
            }}
          />
        )}
      </Box>
      <Text
        fontSize="xs"
        w="40px"
        textAlign="right"
        fontWeight="bold"
        style={{
          color: percentile !== null && textColor ? textColor : undefined,
        }}
        color={percentile !== null ? undefined : "gray.400"}
      >
        {percentile !== null ? ordinalSuffix(Math.round(percentile)) : "—"}
      </Text>
    </HStack>
  )
}

function QuizListView({
  completedAttempts,
  expandedQuizId,
  onToggleQuiz,
  onSubjectClick,
}: {
  completedAttempts: QuizSummaryForStudent[]
  expandedQuizId: string | null
  onToggleQuiz: (quizId: string, attemptId: string) => void
  onSubjectClick: (subjectId: string, subjectName: string) => void
}) {
  return (
    <Box maxH="480px" overflowY="auto">
      {/* X-axis labels */}
      <HStack gap={3} px={2} mb={1}>
        <Box w="160px" />
        <Flex flex={1} justify="space-between" px={0}>
          <Text fontSize="xs" color="gray.400">
            0
          </Text>
          <Text fontSize="xs" color="gray.400">
            50th
          </Text>
          <Text fontSize="xs" color="gray.400">
            100th
          </Text>
        </Flex>
        <Box w="40px" />
      </HStack>

      {completedAttempts.map((attempt) => {
        const isExpanded = expandedQuizId === attempt.quiz_id
        const visiblePercentile = attempt.show_percentile
          ? attempt.percentile
          : null
        const isClickable =
          attempt.show_percentile && attempt.attempt_id !== null

        return (
          <Box key={attempt.quiz_id}>
            <LollipopRow
              label={attempt.quiz_name}
              percentile={visiblePercentile}
              isPending={visiblePercentile === null}
              isClickable={isClickable}
              onClick={
                isClickable
                  ? () => onToggleQuiz(attempt.quiz_id, attempt.attempt_id!)
                  : undefined
              }
            />
            {isExpanded && isClickable && (
              <ExpandedSubjects
                quizId={attempt.quiz_id}
                attemptId={attempt.attempt_id!}
                onSubjectClick={onSubjectClick}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}

function ExpandedSubjects({
  quizId,
  attemptId,
  onSubjectClick,
}: {
  quizId: string
  attemptId: string
  onSubjectClick: (subjectId: string, subjectName: string) => void
}) {
  const { data, isLoading } = useQuery<ScoreDistributionResponse>({
    queryKey: ["score-distribution", quizId, attemptId],
    queryFn: () => QuizzesService.getScoreDistribution({ quizId, attemptId }),
  })

  if (isLoading) {
    return (
      <Box pl={8} py={1}>
        <Text fontSize="xs" color="gray.400">
          Loading subjects…
        </Text>
      </Box>
    )
  }

  if (!data?.subjects?.length) {
    return (
      <Box pl={8} py={1}>
        <Text fontSize="xs" color="gray.400">
          No subject breakdown available
        </Text>
      </Box>
    )
  }

  return (
    <Stack gap={0}>
      {data.subjects.map((subject) => (
        <SubjectRow
          key={subject.subject_id}
          subjectId={subject.subject_id}
          subjectName={subject.subject_name}
          percentile={subject.student_percentile}
          onSubjectClick={onSubjectClick}
        />
      ))}
    </Stack>
  )
}

function SubjectHistoryView({
  subjectId,
  subjectName,
  onBack,
}: {
  subjectId: string
  subjectName: string
  onBack: () => void
}) {
  const { data, isLoading } = useQuery<SubjectPercentileHistoryResponse>({
    queryKey: ["subject-percentile-history", subjectId],
    queryFn: () => DashboardService.getSubjectPercentileHistory({ subjectId }),
  })

  return (
    <Stack gap={3}>
      <HStack>
        <Text
          fontSize="sm"
          color="blue.500"
          cursor="pointer"
          _hover={{ textDecoration: "underline" }}
          onClick={onBack}
        >
          ← All quizzes
        </Text>
        <Heading size="sm">{subjectName}</Heading>
      </HStack>

      {isLoading && (
        <Text fontSize="sm" color="gray.400">
          Loading…
        </Text>
      )}

      {data && (
        <Box maxH="480px" overflowY="auto">
          <HStack gap={3} px={2} mb={1}>
            <Box w="160px" />
            <Flex flex={1} justify="space-between">
              <Text fontSize="xs" color="gray.400">
                0
              </Text>
              <Text fontSize="xs" color="gray.400">
                50th
              </Text>
              <Text fontSize="xs" color="gray.400">
                100th
              </Text>
            </Flex>
            <Box w="40px" />
          </HStack>
          {data.entries.map((entry) => (
            <LollipopRow
              key={entry.attempt_id}
              label={entry.quiz_name}
              percentile={entry.student_percentile}
              isPending={entry.student_percentile === null}
              isClickable={false}
            />
          ))}
          {data.entries.length === 0 && (
            <Text fontSize="sm" color="gray.400" px={2}>
              No data for this subject yet.
            </Text>
          )}
        </Box>
      )}
    </Stack>
  )
}

const PERCENTILE_OPT_IN_STORAGE_KEY = "percentile-ranking-opt-in"

function readPercentileOptIn(): boolean {
  if (typeof window === "undefined") return false

  try {
    return localStorage.getItem(PERCENTILE_OPT_IN_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

export function PercentileRankingCard({
  completedAttempts,
}: {
  completedAttempts: QuizSummaryForStudent[]
}) {
  const hasVisiblePercentile = completedAttempts.some(
    (attempt) =>
      attempt.show_percentile && typeof attempt.percentile === "number",
  )
  const [view, setView] = useState<"quiz-list" | "subject-history">("quiz-list")
  const [expandedQuizId, setExpandedQuizId] = useState<string | null>(null)
  const [selectedSubject, setSelectedSubject] = useState<{
    id: string
    name: string
  } | null>(null)
  const [percentileOptIn, setPercentileOptIn] = useState(readPercentileOptIn)

  function handleToggleQuiz(quizId: string, _attemptId: string) {
    if (expandedQuizId === quizId) {
      setExpandedQuizId(null)
    } else {
      setExpandedQuizId(quizId)
    }
  }

  function handleSubjectClick(subjectId: string, subjectName: string) {
    setSelectedSubject({ id: subjectId, name: subjectName })
    setView("subject-history")
  }

  function handleBack() {
    setView("quiz-list")
    setSelectedSubject(null)
  }

  function handleOptInChange(checked: boolean) {
    setPercentileOptIn(checked)

    if (typeof window === "undefined") return

    try {
      if (checked) {
        localStorage.setItem(PERCENTILE_OPT_IN_STORAGE_KEY, "true")
      } else {
        localStorage.removeItem(PERCENTILE_OPT_IN_STORAGE_KEY)
      }
    } catch {
      // Ignore storage failures and keep the UI usable.
    }
  }

  if (!hasVisiblePercentile) {
    return null
  }

  return (
    <Card.Root p={6} variant="outline">
      <Stack gap={4}>
        <Heading size="sm">Percentile Ranking</Heading>
        <Checkbox
          checked={percentileOptIn}
          onCheckedChange={({ checked }) => handleOptInChange(Boolean(checked))}
        >
          Show percentile ranks
        </Checkbox>

        {percentileOptIn ? (
          <>
            {view === "subject-history" && selectedSubject ? (
              <SubjectHistoryView
                subjectId={selectedSubject.id}
                subjectName={selectedSubject.name}
                onBack={handleBack}
              />
            ) : (
              <QuizListView
                completedAttempts={completedAttempts}
                expandedQuizId={expandedQuizId}
                onToggleQuiz={handleToggleQuiz}
                onSubjectClick={handleSubjectClick}
              />
            )}

            {view === "quiz-list" && completedAttempts.length > 0 && (
              <Text fontSize="xs" color="fg.muted" textAlign="center">
                Dashed rows - percentile available once 100+ students complete
                the quiz
              </Text>
            )}
          </>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            Opt in to view percentile ranks from your completed quizzes.
          </Text>
        )}
      </Stack>
    </Card.Root>
  )
}
